/**
 * MantenimientoDatosCard — limpieza de datos operativos antiguos.
 *
 * Vive dentro de la pantalla Saldos, junto a cierre, liquidación e historial.
 *
 * Flujo: revisar (vista previa, solo lectura) → escribir LIMPIAR → confirmar.
 * El botón final permanece deshabilitado hasta que la confirmación coincide,
 * y durante la ejecución se bloquea para impedir el doble clic.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button } from '../../../components/ui';
import { AlertTriangle, Database, RefreshCw, ShieldCheck, XCircle } from 'lucide-react';
import {
  OperacionLimpiezaEstado,
  SeguimientoOperacion,
  buscarOperacionActiva,
  cancelarOperacion,
  consultarEstado,
  esConflictoDeCancelacion,
  iniciarLimpiezaReal,
  iniciarSimulacro,
  obtenerPreview,
  operacionDelConflicto,
  progresoVisible,
  textoDeEstado,
  tiempoTranscurrido,
} from '../../../services/limpiezaDatosService';

const PALABRA_CONFIRMACION = 'LIMPIAR';

interface SorteoSinGanador {
  sorteo_id: string;
  sorteo: string;
  fecha: string;
  tickets: number;
}

interface Descuadre {
  local_id?: string;
  consolidador_id?: string;
  local: string;
  saldo_actual: string;
  suma_movimientos: string;
  diferencia: string;
}

interface PlanApertura {
  local_id: string;
  local: string;
  saldo_actual: string;
  movimientos_a_eliminar: number;
  movimientos_a_conservar: number;
  apertura: string | null;
  saldo_final_esperado: string;
  saldo_se_conserva: boolean;
  sin_movimientos_purgados: boolean;
}

interface DiagnosticoInterno {
  tickets_abiertos: {
    total: number;
    anteriores_al_corte: number;
    por_local: { local_id: string; local: string; tickets: number; anteriores_al_corte: number }[];
  };
  conciliacion_libro_b: { cuadra: boolean; descuadres: Descuadre[]; total_descuadres: number };
  conciliacion_libro_a: { cuadra: boolean; descuadres: Descuadre[]; total_descuadres: number };
  dias_sin_snapshot: string[];
  total_dias_sin_snapshot: number;
}

interface PreviewLimpieza {
  ahora_costa_rica: string;
  fecha_limite: string;
  dias_retencion: number;
  motor_bd: string;
  registros_por_tabla: Record<string, number>;
  total_registros: number;
  tamano_bd_bytes: number | null;
  sorteos_sin_ganador: SorteoSinGanador[];
  diagnostico: DiagnosticoInterno;
  conciliacion_ok: boolean;
  apertura_por_local: PlanApertura[];
  respaldo: { ok: boolean; motivo?: string | null; creado_en?: string; horas_antiguedad?: number };
  advertencias: string[];
  puede_ejecutar: boolean;
  limpieza_en_curso: boolean;
  ultima_limpieza: { fecha: string | null; fecha_limite: string; usuario_id: string } | null;
}

const ETIQUETAS: Record<string, string> = {
  tickets: 'Tickets',
  jugadas: 'Jugadas',
  tickets_premiados: 'Premios liquidados',
  validaciones_sinpe: 'Validaciones SINPE',
  lineas_lista_manual: 'Líneas de listas manuales',
  saldo_movimientos: 'Movimientos de saldo',
  saldo_movimientos_consolidados: 'Movimientos consolidados (Libro A)',
  caja_movimientos: 'Movimientos de caja',
  caja_sesion_logs: 'Bitácora de sesiones de caja',
  cierres_detalle: 'Detalles de cierres',
  auditoria_logs: 'Registros de auditoría',
  sesiones_activas: 'Sesiones vencidas',
};

const fmt = (n: number) => n.toLocaleString('es-CR');

const fmtBytes = (b: number | null) => {
  if (b == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = b; let i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(1)} ${u[i]}`;
};

/**
 * Progreso de la operación en curso.
 *
 * El porcentaje sale TAL CUAL del backend. Estimarlo aquí —por etapa, por
 * tiempo transcurrido— produciría una barra que avanza sola mientras el
 * servidor está atascado, que es peor que no tener barra.
 */
const PanelProgreso: React.FC<{
  estado: OperacionLimpiezaEstado;
  aviso: string | null;
  cancelando: boolean;
  onCancelar: () => void;
}> = ({ estado, aviso, cancelando, onCancelar }) => {
  const { porcentaje, registros, etapa } = progresoVisible(estado);
  const fallo = estado.estado === 'FALLIDO';
  const exito = estado.estado === 'COMPLETADO' || estado.estado === 'SIMULACRO_COMPLETADO';
  const tono = fallo
    ? 'bg-red-50 border-red-300'
    : exito ? 'bg-green-50 border-green-300' : 'bg-blue-50 border-blue-200';

  return (
    <div className={`mt-3 p-3 border rounded-lg text-sm ${tono}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 flex items-center gap-2">
            {fallo ? <XCircle size={16} className="text-red-600" />
              : exito ? <ShieldCheck size={16} className="text-green-600" />
              : <RefreshCw size={16} className="animate-spin text-blue-600" />}
            {textoDeEstado(estado)}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            {estado.es_simulacro ? 'Simulacro (no borra nada)' : 'Limpieza real'}
            {' · '}{estado.estado}
            {estado.fecha_limite && <> · corte {estado.fecha_limite}</>}
          </p>
        </div>
        {estado.cancelable && (
          <Button variant="secondary" onClick={onCancelar} disabled={cancelando}>
            {cancelando ? 'Cancelando…' : 'Cancelar'}
          </Button>
        )}
      </div>

      {estado.activa && (
        <div className="mt-2">
          <div className="h-2 bg-white/70 rounded-full overflow-hidden border">
            <div className="h-full bg-blue-600 transition-all duration-500"
                 style={{ width: `${porcentaje}%` }} />
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-gray-700">
            <span className="font-medium">{porcentaje.toFixed(0)}%</span>
            {etapa && <span>{etapa}</span>}
            {estado.etapa_actual && <span>{estado.etapa_actual}</span>}
            {registros && <span>{registros} registros</span>}
            <span>{tiempoTranscurrido(estado.segundos_transcurridos)}</span>
          </div>
        </div>
      )}

      {estado.mensaje && (
        <p className="mt-1.5 text-xs text-gray-700">{estado.mensaje}</p>
      )}
      {fallo && (estado.mensaje_error || estado.error) && (
        <p className="mt-1.5 text-xs text-red-700 whitespace-pre-line">
          {estado.mensaje_error || estado.error}
        </p>
      )}
      {aviso && (
        <p className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {aviso}
        </p>
      )}
    </div>
  );
};

export const MantenimientoDatosCard: React.FC<{ onLimpiezaCompleta?: () => void }> = ({
  onLimpiezaCompleta,
}) => {
  const [preview, setPreview] = useState<PreviewLimpieza | null>(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [confirmacion, setConfirmacion] = useState('');
  const [error, setError] = useState('');
  const [avisoPolling, setAvisoPolling] = useState<string | null>(null);

  // Estado de la operación en curso. `null` = ninguna en seguimiento.
  const [operacion, setOperacion] = useState<OperacionLimpiezaEstado | null>(null);
  const [simulacroOk, setSimulacroOk] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  // Solo cubre el envío del POST, no toda la operación: el POST responde en
  // milisegundos y el progreso se sigue por polling.
  const [enviando, setEnviando] = useState(false);

  const vivo = useRef(true);
  const seguimiento = useRef<SeguimientoOperacion | null>(null);

  const detenerSeguimiento = useCallback(() => {
    seguimiento.current?.detener();
    seguimiento.current = null;
  }, []);

  // Un solo seguimiento vivo: si ya había uno, se detiene antes de crear otro.
  const seguir = useCallback((operacionId: string) => {
    detenerSeguimiento();
    const s = new SeguimientoOperacion(operacionId, {
      onEstado: (e) => { if (vivo.current) setOperacion(e); },
      onErrorTemporal: (m) => { if (vivo.current) setAvisoPolling(m); },
      onFinal: (e) => {
        if (!vivo.current) return;
        setOperacion(e);
        if (e.estado === 'SIMULACRO_COMPLETADO') setSimulacroOk(true);
        if (e.estado === 'COMPLETADO') {
          setPreview(null);
          setConfirmacion('');
          onLimpiezaCompleta?.();
        }
      },
    });
    seguimiento.current = s;
    void s.iniciar();
  }, [detenerSeguimiento, onLimpiezaCompleta]);

  const revisar = useCallback(async () => {
    setCargandoPreview(true);
    setError('');
    setConfirmacion('');
    setSimulacroOk(false);
    try {
      const data = await obtenerPreview<PreviewLimpieza>();
      if (vivo.current) setPreview(data);
    } catch (e: any) {
      if (vivo.current) setError(e?.message || 'No se pudo obtener la vista previa');
    } finally {
      if (vivo.current) setCargandoPreview(false);
    }
  }, []);

  // Al montar: ¿hay una operación corriendo? Recargar la página durante una
  // limpieza no debe perder el seguimiento.
  useEffect(() => {
    vivo.current = true;
    void (async () => {
      try {
        const activa = await buscarOperacionActiva();
        if (!vivo.current) return;
        if (activa) {
          setOperacion(activa);
          seguir(activa.operacion_id);
          return;
        }
      } catch {
        // Sin operación activa detectable: se sigue al flujo normal.
      }
    })();
    return () => {
      vivo.current = false;
      detenerSeguimiento();      // sin timers huérfanos tras desmontar
    };
  }, [seguir, detenerSeguimiento]);

  /**
   * Arranca simulacro o limpieza real. El POST se envía UNA vez.
   *
   * Si el backend responde 409 porque ya hay una operación activa, no se
   * muestra un error: se adopta esa operación y se sigue su progreso. Volver a
   * enviar el POST podría lanzar una segunda limpieza.
   */
  const arrancar = useCallback(async (real: boolean) => {
    if (enviando || operacion?.activa) return;
    setEnviando(true);
    setError('');
    setAvisoPolling(null);
    try {
      const r = real ? await iniciarLimpiezaReal() : await iniciarSimulacro();
      if (!vivo.current) return;
      setSimulacroOk(false);
      setOperacion(null);
      seguir(r.operacion_id);
    } catch (e: any) {
      if (!vivo.current) return;
      const existente = operacionDelConflicto(e);
      if (existente) {
        setError('');
        seguir(existente.operacion_id);   // reengancharse, no reintentar
      } else {
        setError(e?.message || 'No se pudo iniciar la operación');
      }
    } finally {
      if (vivo.current) setEnviando(false);
    }
  }, [enviando, operacion, seguir]);

  const simular = useCallback(() => arrancar(false), [arrancar]);
  const confirmar = useCallback(() => arrancar(true), [arrancar]);

  const cancelar = useCallback(async () => {
    if (!operacion || cancelando) return;
    if (!window.confirm('¿Cancelar la operación? No se eliminará ningún dato.')) return;
    setCancelando(true);
    try {
      await cancelarOperacion(operacion.operacion_id);
      // El estado real lo trae el polling: aquí no se declara nada cancelado.
    } catch (e: any) {
      if (!vivo.current) return;
      if (esConflictoDeCancelacion(e)) {
        // Ya está APLICANDO: se muestra el motivo del backend y se sigue
        // monitoreando. El botón desaparece solo cuando `cancelable` sea false.
        setAvisoPolling(e?.message || 'La operación ya no puede cancelarse.');
        try {
          const fresco = await consultarEstado(operacion.operacion_id);
          if (vivo.current) setOperacion(fresco);
        } catch { /* el polling lo recogerá */ }
      } else {
        setError(e?.message || 'No se pudo solicitar la cancelación');
      }
    } finally {
      if (vivo.current) setCancelando(false);
    }
  }, [operacion, cancelando]);

  const enCurso = Boolean(operacion?.activa);
  const confirmacionValida = confirmacion.trim().toUpperCase() === PALABRA_CONFIRMACION;
  const habilitado =
    Boolean(preview?.puede_ejecutar) && simulacroOk && confirmacionValida &&
    !enviando && !enCurso;

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="p-2 bg-slate-100 rounded-lg shrink-0">
          <Database size={20} className="text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">Mantenimiento de datos</h3>
          <p className="text-sm text-gray-500 mt-1">
            Elimina información operativa con más de 7 días de antigüedad. Los saldos,
            usuarios, vendedores, restringidos, paquetes de reglas y configuraciones se
            conservarán.
          </p>

          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-line">
              {error}
            </div>
          )}

          {operacion && (
            <PanelProgreso
              estado={operacion}
              aviso={avisoPolling}
              cancelando={cancelando}
              onCancelar={cancelar}
            />
          )}

          {!preview && !enCurso && (
            <div className="mt-3">
              <Button onClick={revisar} disabled={cargandoPreview}>
                {cargandoPreview ? 'Revisando…' : 'Revisar datos para limpiar'}
              </Button>
            </div>
          )}

          {preview && (
            <div className="mt-4 space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="text-blue-900">
                  <strong>Fecha de corte: {preview.fecha_limite}</strong> (hora de Costa Rica)
                </p>
                <p className="text-blue-800 mt-1">
                  Se eliminará únicamente lo <strong>anterior</strong> a esa fecha. Los últimos{' '}
                  {preview.dias_retencion} días se conservan completos.
                </p>
                <p className="text-blue-700 text-xs mt-1">
                  Motor: {preview.motor_bd} · Tamaño actual: {fmtBytes(preview.tamano_bd_bytes)}
                  {preview.ultima_limpieza?.fecha && (
                    <> · Última limpieza: {new Date(preview.ultima_limpieza.fecha).toLocaleString('es-CR')}</>
                  )}
                </p>
              </div>

              <div className="border rounded-lg divide-y">
                {Object.entries(preview.registros_por_tabla).map(([k, v]) => (
                  <div key={k} className="flex justify-between px-3 py-2 text-sm">
                    <span className="text-gray-600">{ETIQUETAS[k] ?? k}</span>
                    <span className={v > 0 ? 'font-medium text-gray-900' : 'text-gray-400'}>
                      {fmt(v)}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between px-3 py-2 text-sm bg-gray-50">
                  <span className="font-medium text-gray-700">Total</span>
                  <span className="font-bold text-gray-900">{fmt(preview.total_registros)}</span>
                </div>
              </div>

              {preview.diagnostico && (
                <div className="p-3 border rounded-lg text-sm space-y-1">
                  <p className="font-medium text-gray-700 flex items-center gap-2">
                    <ShieldCheck size={16} className="text-gray-500" />
                    Diagnóstico automático
                  </p>
                  <p className={preview.diagnostico.conciliacion_libro_b.cuadra
                    ? 'text-green-700' : 'text-amber-700'}>
                    Historial antiguo:{' '}
                    {preview.diagnostico.conciliacion_libro_b.cuadra
                      ? 'todos los locales cuadran'
                      : `${preview.diagnostico.conciliacion_libro_b.total_descuadres} local(es) descuadrados — se eliminan y la apertura los sustituye`}
                  </p>
                  <p className={preview.diagnostico.conciliacion_libro_a.cuadra
                    ? 'text-green-700' : 'text-amber-700'}>
                    Libro A (Mini Bancas):{' '}
                    {preview.diagnostico.conciliacion_libro_a.cuadra
                      ? 'cuadra' : `${preview.diagnostico.conciliacion_libro_a.total_descuadres} descuadrado(s) — los sustituye la apertura`}
                  </p>
                  <p className="text-gray-600">
                    Tickets sin ganador (abiertos): {fmt(preview.diagnostico.tickets_abiertos.total)}
                    {' '}({fmt(preview.diagnostico.tickets_abiertos.anteriores_al_corte)} anteriores al corte, se conservarán)
                  </p>
                  <p className="text-gray-600">
                    Días a purgar sin snapshot: {fmt(preview.diagnostico.total_dias_sin_snapshot)}
                    {preview.diagnostico.total_dias_sin_snapshot > 0 &&
                      ' — se crearán automáticamente; si alguno falla, la limpieza se cancela sola'}
                  </p>
                </div>
              )}

              {preview.apertura_por_local?.length > 0 && (
                <div className="border rounded-lg overflow-x-auto">
                  <p className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border-b">
                    Saldo preservado por local ·{' '}
                    <span className="font-normal text-gray-500">
                      apertura = saldo actual − movimientos conservados
                    </span>
                  </p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left border-b text-gray-600">
                        <th className="py-1.5 px-2">Local</th>
                        <th className="py-1.5 px-2 text-right">Saldo actual</th>
                        <th className="py-1.5 px-2 text-right">Mov. a eliminar</th>
                        <th className="py-1.5 px-2 text-right">Mov. a conservar</th>
                        <th className="py-1.5 px-2 text-right">Apertura</th>
                        <th className="py-1.5 px-2 text-right">Saldo final</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {preview.apertura_por_local.map((p) => (
                        <tr key={p.local_id}
                            className={p.sin_movimientos_purgados ? 'text-gray-400' : ''}>
                          <td className="py-1.5 px-2">{p.local}</td>
                          <td className="py-1.5 px-2 text-right">{p.saldo_actual}</td>
                          <td className="py-1.5 px-2 text-right">{fmt(p.movimientos_a_eliminar)}</td>
                          <td className="py-1.5 px-2 text-right">{fmt(p.movimientos_a_conservar)}</td>
                          <td className="py-1.5 px-2 text-right font-medium">
                            {p.sin_movimientos_purgados ? 'sin purgar' : p.apertura}
                          </td>
                          <td className={`py-1.5 px-2 text-right font-semibold ${
                            p.saldo_se_conserva ? 'text-green-700' : 'text-red-700'}`}>
                            {p.saldo_final_esperado}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="px-3 py-2 text-xs text-green-700 bg-green-50 border-t">
                    El saldo final de cada local será idéntico al saldo actual.
                  </p>
                </div>
              )}

              {preview.sorteos_sin_ganador.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm">
                  <p className="text-amber-900 font-medium flex items-center gap-2">
                    <AlertTriangle size={16} /> Se conservarán por no tener ganador
                  </p>
                  <ul className="mt-1 space-y-0.5 text-amber-800">
                    {preview.sorteos_sin_ganador.slice(0, 10).map((s) => (
                      <li key={`${s.sorteo_id}-${s.fecha}`}>
                        {s.fecha} · {s.sorteo} · {fmt(s.tickets)} ticket(s)
                      </li>
                    ))}
                  </ul>
                  {preview.sorteos_sin_ganador.length > 10 && (
                    <p className="text-amber-700 text-xs mt-1">
                      y {preview.sorteos_sin_ganador.length - 10} más…
                    </p>
                  )}
                </div>
              )}

              {preview.advertencias.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-900">
                  <ul className="space-y-1">
                    {preview.advertencias.map((a) => <li key={a}>• {a}</li>)}
                  </ul>
                </div>
              )}

              <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-sm text-red-800">
                <p className="font-medium flex items-center gap-2">
                  <AlertTriangle size={16} /> Esta operación no se puede deshacer desde la interfaz.
                </p>
                <p className="mt-1">
                  La recuperación solo es posible restaurando el respaldo del servidor.
                </p>
              </div>

              {preview.puede_ejecutar ? (
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg text-sm">
                    <p className="text-gray-700 font-medium">
                      Paso 1 · Probar sin borrar
                    </p>
                    <p className="text-gray-500 mt-0.5">
                      Verifica la aritmética del plan: comprueba que los saldos
                      quedarían idénticos, sin ejecutar ningún borrado.
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      {/* `disabled` mientras se envía o hay algo en curso: es
                          la guardia contra el doble clic, junto con la de
                          `arrancar()`. */}
                      <Button variant="secondary" onClick={simular}
                              disabled={enviando || enCurso}>
                        {enviando ? (
                          <span className="flex items-center gap-2">
                            <RefreshCw size={16} className="animate-spin" /> Iniciando…
                          </span>
                        ) : 'Probar sin borrar (simulacro)'}
                      </Button>
                      {simulacroOk && (
                        <span className="text-sm text-green-700 flex items-center gap-1">
                          <ShieldCheck size={16} /> Verificaciones correctas
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={simulacroOk ? '' : 'opacity-50'}>
                    <label className="block text-sm text-gray-700 mb-1">
                      Paso 2 · Escriba <strong>{PALABRA_CONFIRMACION}</strong> para
                      habilitar el botón:
                    </label>
                    <input
                      value={confirmacion}
                      onChange={(e) => setConfirmacion(e.target.value)}
                      disabled={enviando || enCurso || !simulacroOk}
                      placeholder={PALABRA_CONFIRMACION}
                      className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No es posible ejecutar la limpieza en este momento.
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={confirmar} disabled={!habilitado}>
                  {enviando ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw size={16} className="animate-spin" /> Iniciando…
                    </span>
                  ) : 'Confirmar limpieza'}
                </Button>
                <Button variant="secondary" onClick={revisar}
                        disabled={enviando || enCurso || cargandoPreview}>
                  Actualizar vista previa
                </Button>
                <Button variant="secondary" onClick={() => setPreview(null)}
                        disabled={enviando || enCurso}>
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default MantenimientoDatosCard;
